import type { AdSet } from "~/ui-rtk/api/types/paid-media.types"

/**
 * True when a Meta ad set is a Profile Visit target — either its
 * destination is the Instagram profile or its performance goal is
 * PROFILE_VISIT. Mirrors the server's `isProfileVisitTarget` resolution in
 * `meta-paid-media.service.ts`, so any decision the UI makes on this flag
 * (forced VIEW_INSTAGRAM_PROFILE CTA, no destination URL needed) matches
 * what the blast cascade will do.
 */
export function isProfileVisitAdSet(
  adSet: Pick<AdSet, "destination_type" | "optimization_goal"> | undefined,
): boolean {
  if (!adSet) return false
  return (
    adSet.destination_type === "INSTAGRAM_PROFILE" ||
    adSet.optimization_goal === "PROFILE_VISIT"
  )
}

/**
 * Normalize common paste mistakes in a destination URL: doubled schemes
 * (`https://https://…`), a missing colon (`https//…`), and a bare domain
 * (`shop.example.com/x` → `https://shop.example.com/x`).
 */
export function normalizeUrl(raw: string): string {
  let v = raw.trim()
  if (!v) return v
  v = v.replace(/^https?:\/\/(?=https?[:/])/i, "")
  v = v.replace(/^(https?)\/\//i, "$1://")
  if (!/^https?:\/\//i.test(v) && /^[\w.-]+\.[a-z]{2,}/i.test(v)) {
    v = `https://${v}`
  }
  return v
}

/**
 * True when `value` (after {@link normalizeUrl}) parses as an absolute
 * http(s) URL with a host. Used to gate launches that need a destination
 * URL so the job doesn't fail server-side on Meta's "link field is
 * required" (subcode 2061015).
 */
export function isValidHttpUrl(value: string): boolean {
  const normalized = normalizeUrl(value)
  if (!normalized) return false
  try {
    const url = new URL(normalized)
    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      url.hostname.includes(".")
    )
  } catch {
    return false
  }
}
